/**
 * Converts a decimal amount string to wei (smallest unit) as a string.
 * Handles fractional amounts safely using BigInt arithmetic.
 *
 * @param amount - Amount as string (e.g., "42.5", "100", "0.001")
 * @param decimals - Number of decimals (default: 18 for ETH/POL)
 * @returns Wei amount as string (e.g., "42500000000000000000")
 *
 * @example
 * toWei("42.5") // "42500000000000000000"
 * toWei("100")  // "100000000000000000000"
 * toWei("0.001") // "1000000000000000"
 */
export function toWei(amount: string, decimals = 18): string {
  if (!amount || amount.trim() === '') {
    return '0';
  }

  // Normalize: remove leading/trailing whitespace
  const normalized = amount.trim();

  // Validate: only digits, optional single decimal point, optional leading minus
  if (!/^-?\d*\.?\d*$/.test(normalized) || normalized === '.' || normalized === '-.') {
    throw new Error(`Invalid amount format: "${amount}"`);
  }

  // Handle negative amounts
  const isNegative = normalized.startsWith('-');
  const absoluteAmount = isNegative ? normalized.slice(1) : normalized;

  // Split into integer and fractional parts
  const [integerPart = '0', fractionalPart = ''] = absoluteAmount.split('.');

  // Validate fractional part doesn't exceed decimals
  if (fractionalPart.length > decimals) {
    throw new Error(
      `Fractional part exceeds ${decimals} decimals: "${amount}" has ${fractionalPart.length} decimals`
    );
  }

  // Pad fractional part to exactly `decimals` digits
  const paddedFractional = fractionalPart.padEnd(decimals, '0');

  // Combine integer and fractional parts
  const combined = integerPart + paddedFractional;

  // Remove leading zeros (but keep at least one digit)
  const trimmed = combined.replace(/^0+/, '') || '0';

  // Convert to BigInt and back to string to validate
  const result = BigInt(trimmed).toString();

  return isNegative && result !== '0' ? `-${result}` : result;
}

/**
 * Builds a MetaMask deep link URL for sending tokens.
 *
 * @param toAddress - Recipient address
 * @param amount - Amount as string (will be converted to wei)
 * @param chainId - Chain ID (default: 137 for Polygon)
 * @param decimals - Token decimals (default: 18)
 * @returns MetaMask deep link URL
 */
export function buildMetaMaskDeepLink(
  toAddress: string,
  amount: string,
  chainId = 137,
  decimals = 18
): string {
  const weiAmount = toWei(amount, decimals);
  return `https://metamask.app.link/send/${toAddress}@${chainId}?value=${weiAmount}`;
}
